import { useEffect, useRef, useState } from 'react';
import { useRigg } from '@/data/RiggProvider.tsx';
import { useAgentChat, type ChatLine } from '@/data/agentChat.ts';
import { useAttachments } from '@/lib/uploads.ts';
import { cn } from '@/lib/format.ts';
import { AttachButton, AttachmentPills } from './Attachments.tsx';
import { Markdown } from './Markdown.tsx';
import { Button, Select, Textarea, Tip, useToast } from './ui.tsx';

const COLLAPSED_KEY = 'rigg:chat:collapsed';

/**
 * The assistant, in a column of its own.
 *
 * Deliberately outside `TasksScreen` and every other screen — it is the same
 * conversation whichever page you are looking at, because "why are so many
 * tasks failing" is a question about the project, not about whatever list
 * happens to be on screen. `Shell` renders it once, next to whatever the
 * route picked.
 *
 * Below `xl` there is no room for a fourth column — the other three tracks
 * need 860px before this one gets a pixel — so the docked panel is `xl:flex`
 * only. It does not disappear there: the same floating button and full-screen
 * sheet the phone layout uses take over, so the assistant is always reachable
 * at every width, just not always parked.
 */
export function AgentChat() {
  const { project, me } = useRigg();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === '1');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  if (!project || !me) return null;

  return (
    <>
      {collapsed ? (
        <aside className="hidden w-11 shrink-0 flex-col items-center border-l border-line bg-aside pt-4 xl:flex">
          <Tip content="Open the assistant" side="left">
            <button
              onClick={() => setCollapsed(false)}
              aria-label="Open the assistant"
              className="rg-focus grid size-8 shrink-0 place-items-center rounded-full text-soft hover:bg-btn hover:text-hi2"
            >
              💬
            </button>
          </Tip>
        </aside>
      ) : (
        <aside className="hidden min-h-0 w-[320px] shrink-0 flex-col border-l border-line bg-aside xl:flex 2xl:w-[360px]">
          <AgentChatPanel projectId={project.id} userId={me.id} onCollapse={() => setCollapsed(true)} />
        </aside>
      )}

      {/*
        Below `lg` the docked aside above never renders at all, so this is
        the only way in: a thumb-reachable button that opens the same panel
        as a full-screen sheet, closed the same way a dialog would be.
      */}
      <button
        onClick={() => setMobileOpen(true)}
        aria-label="Open the assistant"
        className="rg-focus fixed right-4 bottom-24 z-30 grid size-12 place-items-center rounded-full bg-accent text-ink shadow-[0_10px_30px_rgba(10,12,24,0.45)] xl:hidden"
      >
        <span aria-hidden className="text-[19px] leading-none">💬</span>
      </button>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 flex flex-col bg-aside xl:hidden">
          <AgentChatPanel
            projectId={project.id}
            userId={me.id}
            onCollapse={() => setMobileOpen(false)}
            closeLabel="Close the assistant"
            closeIcon="✕"
          />
        </div>
      ) : null}
    </>
  );
}

function AgentChatPanel({
  projectId,
  userId,
  onCollapse,
  closeLabel = 'Collapse the assistant',
  closeIcon = '›',
}: {
  projectId: string;
  userId: string;
  onCollapse: () => void;
  closeLabel?: string;
  closeIcon?: string;
}) {
  const { notify } = useToast();
  const { sessions, activeId, setActiveId, messages, busy, send, newSession, deleteSession, interrupt } =
    useAgentChat(projectId, userId);

  const [draft, setDraft] = useState('');
  const scroller = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const [dragging, setDragging] = useState(false);

  const {
    items: attachments,
    onDrop,
    onDragOver,
    onPaste,
    attach,
    remove: removeAttachment,
    clear: clearAttachments,
    compose,
    uploading,
    hasAttachment,
  } = useAttachments({ projectId, notify });

  useEffect(() => {
    if (!scroller.current) return;
    scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages]);

  const submit = async () => {
    const text = compose(draft.trim());
    if (!text || busy) return;
    setDraft('');
    clearAttachments();
    try {
      await send(text);
    } catch (error) {
      notify('Could not reach the assistant', { body: (error as Error).message, tone: 'bad' });
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-3.5">
        <span className="text-[13.5px] font-[650] text-hi">Assistant</span>
        <span className="flex-1" />
        {sessions.length > 0 ? (
          <Select
            size="sm"
            value={activeId ?? ''}
            onChange={(id) => setActiveId(id)}
            options={sessions.map((s) => ({ value: s.id, label: s.title }))}
            aria-label="Chat session"
            className="max-w-[130px]"
          />
        ) : null}
        <Tip content="New session">
          <button
            onClick={() => void newSession()}
            aria-label="New session"
            className="rg-focus grid size-7 shrink-0 place-items-center rounded-full text-soft hover:bg-btn hover:text-hi2"
          >
            +
          </button>
        </Tip>
        {activeId ? (
          <Tip content="Delete this session">
            <button
              onClick={() => void deleteSession(activeId)}
              aria-label="Delete this session"
              className="rg-focus grid size-7 shrink-0 place-items-center rounded-full text-soft hover:bg-btn hover:text-warnx"
            >
              🗑
            </button>
          </Tip>
        ) : null}
        <Tip content={closeLabel}>
          <button
            onClick={onCollapse}
            aria-label={closeLabel}
            className="rg-focus grid size-7 shrink-0 place-items-center rounded-full text-soft hover:bg-btn hover:text-hi2"
          >
            {closeIcon}
          </button>
        </Tip>
      </div>

      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">
        {messages.length === 0 ? (
          <div className="px-1 py-8 text-center text-[13px] leading-relaxed text-mute">
            Ask it anything about this project — "why are so many tasks failing?", "sign off #42",
            "open a task for the flaky test". It can read the whole board and act on your behalf.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {messages.map((line) => (
              <ChatBubble key={line.id} line={line} />
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-line px-4 py-3">
        <div
          className="relative"
          onDragEnter={(event) => {
            if (event.dataTransfer.types.includes('Files')) setDragging(true);
          }}
          onDragOver={onDragOver}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
          }}
          onDrop={(event) => {
            setDragging(false);
            onDrop(event);
          }}
        >
          <Textarea
            ref={box}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                void submit();
              }
            }}
            onPaste={onPaste}
            placeholder="Ask the assistant… drag in files, or paste a screenshot"
            className="min-h-14"
            aria-label="Message the assistant"
          />
          {dragging ? (
            <div className="rg-anim pointer-events-none absolute inset-0 grid place-items-center rounded-[10px] border-2 border-dashed border-accent bg-app/90 text-[13px] font-semibold text-accent">
              Drop to attach
            </div>
          ) : null}
        </div>
        <AttachmentPills items={attachments} onRemove={removeAttachment} />
        <div className="mt-2 flex items-center gap-2">
          <AttachButton onFiles={attach} disabled={busy} />
          <span className="flex-1" />
          {busy ? (
            <Button size="sm" onClick={interrupt}>
              Stop
            </Button>
          ) : null}
          <Button
            tone="primary"
            size="sm"
            disabled={busy || uploading || (!draft.trim() && !hasAttachment)}
            onClick={() => void submit()}
          >
            {uploading ? 'Uploading…' : busy ? 'Thinking…' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ line }: { line: ChatLine }) {
  if (line.role === 'status') {
    return (
      <div className="flex items-center gap-1.5 px-1 text-[12px] text-mute italic">
        <span className={cn('size-1.5 shrink-0 rounded-full bg-amberx', line.pending && 'animate-pulse')} />
        {line.body}
      </div>
    );
  }

  if (line.role === 'error') {
    return (
      <div className="rounded-lg border border-warnx/25 bg-warnx/6 px-3 py-2 text-[12.5px] leading-relaxed text-warnx">
        {line.body}
      </div>
    );
  }

  const mine = line.role === 'user';
  return (
    <div className={cn('flex', mine && 'justify-end')}>
      <div
        className={cn(
          'max-w-[86%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed',
          mine ? 'bg-accent text-ink whitespace-pre-wrap' : 'border border-line bg-callout text-t3',
        )}
      >
        {/*
          What a person typed is shown exactly as typed — no markdown parsing
          of the human side of the conversation. The assistant's reply is the
          agent-authored half, and the one routines can put formatting in.
        */}
        {mine ? line.body : <Markdown>{line.body}</Markdown>}
        {line.pending ? (
          <span className="rg-live ml-0.5 inline-block size-1.5 rounded-full bg-current align-middle" />
        ) : null}
      </div>
    </div>
  );
}
